import React, { memo, useMemo } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { Link } from 'react-router-dom'
import { Button } from 'src/components/shared/Button'
import { GSIcon } from 'src/components/shared/GSIcon'
import { SectionCard } from 'src/components/shared/SectionCard'
import { buildPartnerDriverStepsPath } from 'src/constants/routes'
import type { PartnerDriverDetails } from 'src/interfaces/partnerDriver'
import { dedupeSteps } from 'src/utils/partnerDriver/dedupeSteps'

interface Props {
  driver: PartnerDriverDetails
  /** Forwarded so the steps page's Back links still lead to the same search results. */
  navigationState: unknown
}

/**
 * The steps table lives on its own page; this card says how much is behind it before the user
 * navigates, as the Schedulers detail page does for its retries history.
 */
const StepsLinkCardComponent = ({ driver, navigationState }: Props) => {
  // Same count the steps page shows (deduped, as Retool did).
  const stepsCount = useMemo(() => {
    return dedupeSteps(driver.collectDataSteps ?? []).length
  }, [driver.collectDataSteps])

  return (
    <SectionCard
      title="Collected data steps"
      subtitle={`${stepsCount} ${stepsCount === 1 ? 'step' : 'steps'}`}
      dataTestId="partner-driver-steps-link-card"
      actions={
        <Link
          to={buildPartnerDriverStepsPath(driver.id)}
          state={navigationState}
          css={tw`no-underline`}
        >
          <Button
            size="small"
            color="grey"
            rightIcon={
              <GSIcon iconName="gs_chevron_right_small" size={20} css={tw`text-shades-1000`} />
            }
            data-testid="partner-driver-open-steps-button"
          >
            Open steps
          </Button>
        </Link>
      }
    >
      <Typography variant="body2" css={tw`text-shades-600`}>
        Review each onboarding step, upload or delete documents, and complete or reset steps.
      </Typography>
    </SectionCard>
  )
}

export const StepsLinkCard = memo(StepsLinkCardComponent)
